import React from 'react';
import { AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { CampaignProcessingStatus } from '../lib/campaignStatusHelper';

interface OverdueBadgeProps {
  formattedLateness: string;
  variant?: 'compact' | 'full';
}

export function OverdueBadge({ formattedLateness, variant = 'full' }: OverdueBadgeProps) {
  if (variant === 'compact') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">
        <AlertTriangle className="w-3.5 h-3.5" />
        TE LAAT
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-red-100 text-red-800 border-2 border-red-400 shadow-sm">
      <AlertTriangle className="w-4 h-4" />
      <span>TE LAAT: {formattedLateness}</span>
    </span>
  );
}

interface CampaignStatusBadgeProps {
  processingStatus: CampaignProcessingStatus;
  statusLabel: string;
  formattedLateness?: string;
  variant?: 'compact' | 'full';
}

export function CampaignStatusBadge({
  processingStatus,
  statusLabel,
  formattedLateness,
  variant = 'compact'
}: CampaignStatusBadgeProps) {
  if (processingStatus === 'on-time') {
    return null;
  }

  if (processingStatus === 'processing') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {statusLabel}
      </span>
    );
  }

  if (processingStatus === 'delayed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-300">
        <Clock className="w-3.5 h-3.5" />
        {variant === 'full' && formattedLateness ? formattedLateness : statusLabel}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">
      <AlertTriangle className="w-3.5 h-3.5" />
      {variant === 'full' && formattedLateness ? `TE LAAT: ${formattedLateness}` : 'TE LAAT'}
    </span>
  );
}

interface ScheduledDateDisplayProps {
  scheduledAt: string;
  isOverdue: boolean;
  processingStatus?: CampaignProcessingStatus;
  statusLabel?: string;
  formattedLateness?: string;
}

export function ScheduledDateDisplay({
  scheduledAt,
  isOverdue,
  processingStatus = 'on-time',
  statusLabel = '',
  formattedLateness
}: ScheduledDateDisplayProps) {
  const date = new Date(scheduledAt);
  const formatted = date.toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  const getTextColor = () => {
    if (processingStatus === 'overdue') return 'text-red-600 font-semibold';
    if (processingStatus === 'delayed') return 'text-amber-600 font-medium';
    if (processingStatus === 'processing') return 'text-blue-600';
    return 'text-gray-600';
  };

  const getIconColor = () => {
    if (processingStatus === 'overdue') return 'text-red-600';
    if (processingStatus === 'delayed') return 'text-amber-600';
    if (processingStatus === 'processing') return 'text-blue-600';
    return '';
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className={`flex items-center gap-1.5 ${getTextColor()}`}>
        <Clock className={`w-4 h-4 ${getIconColor()}`} />
        <span>Ingepland: {formatted}</span>
      </div>
      {processingStatus !== 'on-time' && (
        <CampaignStatusBadge
          processingStatus={processingStatus}
          statusLabel={statusLabel}
          formattedLateness={formattedLateness}
          variant="full"
        />
      )}
    </div>
  );
}
